import { PendingTaskFields } from 'pages/Tasks/types';
import { buildTaskFilterFromClinicUser } from 'pages/Tasks/utils';
import { useMemo, useState } from 'react';
import { FeatureFlagName, GraphQLFetchPolicies } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import {
  ClinicUserFilterType,
  TaskStatus,
  useFindManyTaskCountQuery,
  useFindUniqueClinicUserFilterQuery,
  useSubscribeToPendingTasksSubscription,
  useSubscribeToTaskAssigneeRemovedSubscription,
  useSubscribeToDeletedTasksSubscription,
} from 'shared/types/graphql';

const usePendingTasksCount = (): {
  pendingTasksCount?: number;
  updatedTask?: PendingTaskFields;
} => {
  const { clinicUserId, currentClinicId } = useClinicUser();
  const { isFeatureEnabled, isFeatureFlagsLoading } = useFeatureFlag();
  const [updatedTask, setUpdatedTask] = useState<PendingTaskFields | undefined>();

  const { data: filtersData } = useFindUniqueClinicUserFilterQuery({
    variables: {
      where: {
        clinicId_userId_type: {
          clinicId: currentClinicId || '',
          userId: clinicUserId || '',
          type: ClinicUserFilterType.TaskManager,
        },
      },
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const where = useMemo(() => {
    const filters = buildTaskFilterFromClinicUser(
      filtersData?.findUniqueClinicUserFilter?.filter?.needsAttention,
      false,
      clinicUserId,
    );

    filters.status = { equals: TaskStatus.Open };
    filters.clinicId = { equals: currentClinicId || '' };

    return filters;
  }, [clinicUserId, currentClinicId, filtersData?.findUniqueClinicUserFilter?.filter?.needsAttention]);

  const { data: count, refetch: refetchCount } = useFindManyTaskCountQuery({
    variables: {
      where,
    },
    skip: isFeatureFlagsLoading || !isFeatureEnabled(FeatureFlagName.Tasks) || !where,
    fetchPolicy: GraphQLFetchPolicies.NoCache,
  });

  useSubscribeToPendingTasksSubscription({
    variables: {
      where: {
        clinicId: currentClinicId || '',
        userId: clinicUserId || '',
      },
    },
    onData: (d) => {
      setUpdatedTask(d?.data?.data?.pendingTasksChanged?.node);
      refetchCount();
    },
    skip: isFeatureFlagsLoading || !isFeatureEnabled(FeatureFlagName.Tasks) || !where,
  });

  useSubscribeToTaskAssigneeRemovedSubscription({
    variables: {
      where: {
        clinicId: currentClinicId || '',
        userId: clinicUserId || '',
      },
    },
    onData: (d) => {
      setUpdatedTask(d?.data?.data?.taskAssigneeRemoved?.node);
      refetchCount();
    },
    skip: isFeatureFlagsLoading || !isFeatureEnabled(FeatureFlagName.Tasks) || !where,
  });

  useSubscribeToDeletedTasksSubscription({
    variables: {
      where: {
        clinicId: currentClinicId || '',
        userId: clinicUserId || '',
      },
    },
    onData: (d) => {
      setUpdatedTask(d?.data?.data?.pendingTasksChanged?.node);
      refetchCount();
    },
    skip: isFeatureFlagsLoading || !isFeatureEnabled(FeatureFlagName.Tasks) || !where,
  });

  return {
    pendingTasksCount: count?.findManyTaskCount,
    updatedTask: updatedTask,
  };
};

export default usePendingTasksCount;
